import { Users, Megaphone, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { useFactionsByParty } from '@/hooks/useFactionsByParty'
import { FactionStatusBadge } from './FactionStatusBadge'
import { FactionSatisfactionBar } from './FactionSatisfactionBar'
import type { Faction } from '@/types'

export interface FactionListProps {
  partyId: string
  onSelectFaction?: (factionId: string) => void
  className?: string
}

interface FactionCardProps {
  faction: Faction
  onSelect?: () => void
}

function FactionCard({ faction, onSelect }: FactionCardProps) {
  const hasDemand = !!faction.demand_type

  return (
    <Card
      className={cn(
        'cursor-pointer transition-all hover:bg-accent/50 hover:border-accent',
        onSelect && 'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
      )}
      onClick={onSelect}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onKeyDown={
        onSelect
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onSelect()
              }
            }
          : undefined
      }
    >
      <CardContent className="p-4 space-y-3">
        {/* Header: Name + Status */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="font-medium truncate">{faction.name}</h3>
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
              <Users className="h-3 w-3" />
              <span>Members</span>
              {hasDemand && (
                <>
                  <span className="text-amber-400">•</span>
                  <Megaphone className="h-3 w-3 text-amber-400" />
                  <span className="text-amber-400">Active demand</span>
                </>
              )}
            </div>
          </div>
          <FactionStatusBadge status={faction.status} size="sm" />
        </div>

        {/* Influence bar */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Influence</span>
            <span className="font-medium">{faction.influence}%</span>
          </div>
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-500 rounded-full transition-all"
              style={{ width: `${faction.influence}%` }}
            />
          </div>
        </div>

        {/* Satisfaction bar */}
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Satisfaction</span>
          </div>
          <FactionSatisfactionBar
            satisfaction={faction.satisfaction}
            size="sm"
            showThresholds={false}
            showLabel={true}
          />
        </div>
      </CardContent>
    </Card>
  )
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      <div className="p-3 rounded-full bg-muted/50 mb-3">
        <Users className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="font-medium text-sm mb-1">No factions have formed in your party</h3>
      <p className="text-xs text-muted-foreground max-w-xs">
        Factions emerge when candidates cluster around ideological positions different from party leadership
      </p>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1, 2, 3].map((i) => (
        <Card key={i} className="animate-pulse">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-start justify-between">
              <div className="space-y-2 flex-1">
                <div className="h-4 bg-muted rounded w-3/4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </div>
              <div className="h-5 w-16 bg-muted rounded-full" />
            </div>
            <div className="h-1.5 bg-muted rounded-full" />
            <div className="h-1.5 bg-muted rounded-full" />
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function ErrorState({ error }: { error: Error }) {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      <div className="p-3 rounded-full bg-red-500/10 mb-3">
        <AlertCircle className="h-6 w-6 text-red-500" />
      </div>
      <h3 className="font-medium text-sm mb-1">Failed to load factions</h3>
      <p className="text-xs text-muted-foreground max-w-xs">
        {error.message || 'An error occurred while loading factions'}
      </p>
    </div>
  )
}

export function FactionList({ partyId, onSelectFaction, className }: FactionListProps) {
  const { factions, isLoading, error } = useFactionsByParty(partyId)

  if (isLoading) {
    return <LoadingState />
  }

  if (error) {
    return <ErrorState error={error} />
  }

  if (!factions || factions.length === 0) {
    return <EmptyState />
  }

  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4', className)}>
      {factions.map((faction) => (
        <FactionCard
          key={faction.id}
          faction={faction}
          onSelect={onSelectFaction ? () => onSelectFaction(faction.id) : undefined}
        />
      ))}
    </div>
  )
}

/**
 * Compact list variant for sidebar or summary views.
 */
export interface FactionListCompactProps {
  partyId: string
  onSelectFaction?: (factionId: string) => void
  maxItems?: number
  className?: string
}

export function FactionListCompact({
  partyId,
  onSelectFaction,
  maxItems = 5,
  className,
}: FactionListCompactProps) {
  const { factions, isLoading, error } = useFactionsByParty(partyId)

  if (isLoading) {
    return (
      <div className="space-y-2 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-10 bg-muted rounded" />
        ))}
      </div>
    )
  }

  if (error || !factions || factions.length === 0) {
    return null
  }

  const displayFactions = factions.slice(0, maxItems)
  const hasMore = factions.length > maxItems

  return (
    <div className={cn('space-y-2', className)}>
      {displayFactions.map((faction) => (
        <div
          key={faction.id}
          className={cn(
            'flex items-center justify-between p-2 rounded-md',
            onSelectFaction && 'cursor-pointer hover:bg-accent/50 transition-colors'
          )}
          onClick={onSelectFaction ? () => onSelectFaction(faction.id) : undefined}
          role={onSelectFaction ? 'button' : undefined}
          tabIndex={onSelectFaction ? 0 : undefined}
          onKeyDown={
            onSelectFaction
              ? (e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelectFaction(faction.id)
                  }
                }
              : undefined
          }
        >
          <div className="flex items-center gap-2 min-w-0">
            <FactionStatusBadge status={faction.status} size="sm" showIcon={false} />
            <span className="text-sm truncate">{faction.name}</span>
            {faction.demand_type && (
              <Megaphone className="h-3 w-3 text-amber-400 shrink-0" />
            )}
          </div>
          <span className="text-xs text-muted-foreground shrink-0">
            {faction.satisfaction}%
          </span>
        </div>
      ))}
      {hasMore && (
        <div className="text-xs text-muted-foreground text-center py-1">
          +{factions.length - maxItems} more factions
        </div>
      )}
    </div>
  )
}
